import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { BodySection } from "@/components/ui/BodySection";
import { useBreadcrumbs } from "@/presentation/layout/breadcrumbs_context";
import { ROUTES, merchantPath } from "@/routes";
import {
  fetchMerchantDebugContext,
  type MerchantDebugContext,
} from "@/application/merchants/debug_context_repository";

import { MerchantTimeline } from "./components/MerchantTimeline";

/**
 * One merchant's full action timeline.
 *
 * A page rather than a panel because the timeline is the thing you read for
 * a while — every staff action, lifecycle transition, Stripe event, billing
 * change and support ticket, merged and filterable. The merchant overview
 * shows the most recent few and links here.
 *
 * Merchant-scoped by route, not by query string: every source behind it is
 * indexed by business, and a platform-wide merge would mean scanning the
 * largest tables we have on every load.
 */
export function MerchantTimelineView() {
  const { businessId } = useParams<{ businessId: string }>();
  const [context, setContext] = useState<MerchantDebugContext | null>(null);
  const [error, setError] = useState<string | null>(null);

  useBreadcrumbs([
    { label: "Merchants", to: ROUTES.MERCHANTS },
    ...(businessId
      ? [
          { label: businessId, to: merchantPath(businessId) },
          { label: "Timeline" },
        ]
      : []),
  ]);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    setContext(null);
    setError(null);

    fetchMerchantDebugContext(businessId)
      .then((result) => {
        if (!cancelled) setContext(result);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  if (!businessId) return null;

  return (
    <BodySection title="Timeline">
      {error ? (
        <p className="text-sm text-red-600">Could not load: {error}</p>
      ) : !context ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          {context.partialSources.length > 0 && (
            <p className="mb-4 text-sm text-amber-700">
              Could not load: {context.partialSources.join(", ")}. This timeline
              is incomplete.
            </p>
          )}
          <MerchantTimeline entries={context.timeline} />
        </>
      )}
    </BodySection>
  );
}
